import { ColumnHeaderModel, ColumnModel, GridModel } from "@omegagrid/grid";
import { dates, numbers, utils } from "@omegagrid/core";

export type DelimitedExportOptions = {
	separator: string,
	/** machine readable values: '.' decimals without grouping, ISO dates, no number formats */
	raw?: boolean,
};

type ValueSerializer = (value: unknown) => string;

function toIsoDate(value: unknown): string {
	const date = utils.isNumber(value) ? dates.serialDateToDate(value as number)
		: value instanceof Date ? value
		: null;
	return date ? date.toISOString().substring(0, 10) : String(value);
}

function rawNumber(value: unknown): string {
	if (utils.isNumber(value)) return String(value);
	const parsed = parseFloat(value as string);
	return Number.isFinite(parsed) && utils.isNumericString(String(value)) ? String(parsed) : String(value);
}

function createRawSerializer(column: ColumnModel): ValueSerializer {
	switch (column.type) {
		case 'd': return toIsoDate;
		case 'dt':
		case 'tm': return (value) => value instanceof Date ? value.toISOString() : String(value);
		case 'n':
		case 'g': return rawNumber;
		default: return (value) => String(value);
	}
}

function createFormattedSerializer(column: ColumnModel, model: GridModel): ValueSerializer {
	// mirrors the formatter resolution of CellModel.valueFormatter
	const { formatLocale, fixNumberFormat } = model.options;
	const format = column.format;

	switch (column.type) {
		case 'n':
		case 'g': {
			const formatter = format != null
				? numbers.getNumberFormatter(formatLocale, format, fixNumberFormat)
				: model.options.numberFormatter;
			return (value) => {
				const num = utils.isNumber(value) ? value as number : parseFloat(value as string);
				return Number.isFinite(num) && (utils.isNumber(value) || utils.isNumericString(String(value)))
					? formatter(num) : String(value);
			};
		}
		case 'd': {
			const formatter = format ? dates.getDateFormatter(formatLocale, format) : model.options.dateFormatter;
			return formatter ? (value) => formatter(value as number) : toIsoDate;
		}
		default: {
			const formatter = format != null ? numbers.getNumberFormatter(formatLocale, format, false) : null;
			return formatter ? (value) => formatter(value as any) : (value) => String(value);
		}
	}
}

function escapeField(text: string, separator: string): string {
	return text.includes(separator) || text.includes('"') || text.includes('\n') || text.includes('\r')
		? '"' + text.replace(/"/g, '""') + '"'
		: text;
}

/**
 * Serializes the visible grid content into separator-delimited text (RFC 4180
 * style escaping) straight from the raw row data - no cell models, no SheetJS.
 */
export function gridToDelimitedText(model: GridModel, options: DelimitedExportOptions): string {
	const separator = options.separator;
	const columns = model.visibleColumns;
	const serializers = columns.map(column => options.raw
		? createRawSerializer(column)
		: createFormattedSerializer(column, model));

	const serializeField = (value: unknown, index: number) => {
		if (utils.isObject(value)) value = (value as {v: unknown}).v;
		if (value == null || value === '') return '';
		return escapeField(serializers[index](value), separator);
	};

	const lines = [
		columns.map(column => {
			const header = (column.header as ColumnHeaderModel[])?.[0]?.label ?? column.name ?? '';
			return escapeField(String(header), separator);
		}).join(separator)
	];

	for (const row of model.visibleRows) {
		lines.push(columns.map((column, i) => serializeField(row.data?.[column.data], i)).join(separator));
	}

	return lines.join('\r\n');
}
